import { getAPIBaseUrl } from "@/lib/routeStateUtils"
import i18n from "@/lib/i18n/i18n"

type UploadResult = {
  getUrl: string
  fileName: string
}

type PresignUrlResponse = {
  post_url: string
  post_fields: Record<string, string>
  get_url: string
}

export async function uploadToS3(file: File): Promise<UploadResult> {
  try {
    // Get presigned URL
    const baseUrl =
      window.location.hostname !== "app.trickswift.eu"
        ? getAPIBaseUrl()
        : "https://api.trickswift.eu/production"

    const presignResponse = await fetch(`${baseUrl}/presign_url`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ file_name: file.name }),
    })

    if (!presignResponse.ok) {
      throw new Error(
        `Failed to get presign URL: ${presignResponse.statusText}`,
      )
    }

    const presignData: PresignUrlResponse = await presignResponse.json()
    const formData = new FormData()

    presignData.post_fields["Content-Type"] = file.type

    for (const [key, value] of Object.entries(presignData.post_fields)) {
      formData.append(key, value)
    }

    formData.append("file", file)

    const uploadResponse = await fetch(presignData.post_url, {
      method: "POST",
      body: formData,
      signal: AbortSignal.timeout(300000), // 5 minutes
    })

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text()
      throw new Error(
        `S3 upload failed: ${uploadResponse.status} - ${errorText}`,
      )
    }

    return {
      getUrl: presignData.get_url,
      fileName: file.name,
    }
  } catch (error) {
    console.error("Upload error:", error)
    window.appToaster?.show({
      message: i18n.t("planOrder.fileErrors.uploadFailed"),
      intent: "danger",
      timeout: 3000,
    })
    throw new Error(
      `File upload failed: ${error instanceof Error ? error.message : "Unknown error"}`,
    )
  }
}
